import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { StatsPageResponse } from '../../types';

const excludedPokemonNames = new Set([
  'Bombirdier',
  'Larvesta',
  'Hawlucha',
  'Falinks',
  'Absol',
  'Miltank',
  'Stonjourner',
  'Klawf',
  'Turtonator',
]);

const formOverrides: Record<string, { form: string; key: string }> = {
  'Wooper': { form: 'Paldea', key: 'Wooper-Paldea' },
  'Vulpix': { form: 'Alola', key: 'Vulpix-Alola' },
  'Voltorb': { form: 'Hisui', key: 'Voltorb-Hisui' },
  "Farfetch'd": { form: 'Galar', key: "Farfetch'd-Galar" },
  'Sandshrew': { form: 'Alola', key: 'Sandshrew-Alola' },
  'Meowth': { form: 'Galar', key: 'Meowth-Galar' },
  'Slowpoke': { form: 'Galar', key: 'Slowpoke-Galar' },
  'Zigzagoon': { form: 'Galar', key: 'Zigzagoon-Galar' },
};

const resolveIdentity = (name: string, form: string) => {
  let currentName = name;
  let currentForm = form;
  const knownForms = ['Alola', 'Galar', 'Hisui', 'Paldea'];
  for (const f of knownForms) {
    if (currentName.endsWith(`-${f}`)) {
      currentName = currentName.slice(0, -(f.length + 1));
      currentForm = f;
      break;
    }
  }
  if ((!currentForm || currentForm === 'base') && formOverrides[currentName]) {
    return { name: currentName, ...formOverrides[currentName] };
  }
  const effectiveForm = currentForm && currentForm !== 'base' ? currentForm : '';
  const key = `${currentName}${effectiveForm ? '-' + effectiveForm : ''}`;
  return { name: currentName, form: effectiveForm, key };
};

function calculateQuantile(sortedData: number[], q: number) {
  const pos = (sortedData.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedData[base + 1] !== undefined) {
    return sortedData[base] + rest * (sortedData[base + 1] - sortedData[base]);
  }
  return sortedData[base];
}

// Y-axis tick positions as round, easy-to-glance numbers (multiples of 100 or
// 250). With the axis no longer anchored at 0, Recharts' default ticker drifts
// to awkward offsets; this snaps labels back to a nice grid across the domain.
function niceYTicks(min: number, max: number): number[] {
  const range = max - min;
  if (range <= 0) return [];
  const targetStep = range / 5;
  const candidates: number[] = [];
  for (let exp = 2; exp <= 7; exp += 1) {
    candidates.push(100 * Math.pow(10, exp - 2));
    candidates.push(250 * Math.pow(10, exp - 2));
    candidates.push(500 * Math.pow(10, exp - 2));
  }
  const step = candidates.find((c) => c >= targetStep) ?? candidates[candidates.length - 1];
  const ticks: number[] = [];
  for (let t = Math.ceil(min / step) * step; t <= max; t += step) {
    ticks.push(t);
  }
  return ticks;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const price = data.realCost ?? data.cost;
    return (
      <div style={{ 
        backgroundColor: '#1a1a1a', 
        border: '1px solid #333', 
        padding: '10px', 
        fontSize: '12px', 
        borderRadius: '4px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
      }}>
        <p style={{ margin: '0 0 6px', color: '#888', fontWeight: 600 }}>Sale #{label}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <p style={{ margin: 0, color: '#fff' }}>
            <span style={{ color: '#888' }}>Date:</span> {data.date}
          </p>
          <p style={{ margin: 0, color: '#fff' }}>
            <span style={{ color: '#888' }}>Price:</span> ${price.toLocaleString()}
            {data.clipSide && <span style={{ color: '#888' }}> (clipped to {data.clipSide} edge)</span>}
          </p>
          {data.winner !== 'Guest' && (
            <p style={{ margin: 0, color: '#fff' }}>
              <span style={{ color: '#888' }}>Winner:</span> {data.winner}
            </p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

interface PokemonHistoryChartProps {
  pokemonKey: string;
  pokemonName: string;
  stats: StatsPageResponse;
  cutoffDate?: string;
}

// Chart body only (no outer box / title). Used by the standalone snapshot and
// by the tabbed history dropdown.
const PokemonPriceHistoryChartBody: React.FC<PokemonHistoryChartProps> = ({ pokemonKey, stats, cutoffDate }) => {
  const chartData = useMemo(() => {
    const cutoff = cutoffDate ? new Date(cutoffDate).getTime() : 0;

    // Determine valid (competitive) draft IDs
    const draftStatsMap = new Map<string, { total: number; minBidCount: number; teamCount: number; maxBid: number }>();

    (stats.teams ?? []).forEach((t) => {
      const curr = draftStatsMap.get(t.draft_id) || { total: 0, minBidCount: 0, teamCount: 0, maxBid: 0 };
      curr.teamCount += 1;
      draftStatsMap.set(t.draft_id, curr);
    });

    (stats.auctions ?? []).forEach((a) => {
      if (a.winning_bid !== null) {
        const curr = draftStatsMap.get(a.draft_id) || { total: 0, minBidCount: 0, teamCount: 0, maxBid: 0 };
        curr.total += 1;
        if (a.winning_bid === 100) {
          curr.minBidCount += 1;
        }
        if (a.winning_bid > curr.maxBid) {
          curr.maxBid = a.winning_bid;
        }
        draftStatsMap.set(a.draft_id, curr);
      }
    });

    const validDraftIds = new Set<string>();
    draftStatsMap.forEach((data, id) => {
      if (data.total >= 40 && data.minBidCount <= 3 && data.total === 8 * data.teamCount && data.maxBid <= 12000) {
        validDraftIds.add(id);
      }
    });

    // Map of players for easy name lookup
    const playersMap = new Map<string, string>();
    (stats.players ?? []).forEach(p => {
      if (!p.is_guest) {
        playersMap.set(p.user_id, p.user_name);
      }
    });

    // 1. Gather all sales including auctions and legacy data
    const auctionSales = stats.auctions
      .filter(a => {
        const isMatch = a.winning_bid !== null && validDraftIds.has(a.draft_id) && !excludedPokemonNames.has(a.name) && resolveIdentity(a.name, a.form || '').key === pokemonKey;
        if (!isMatch) return false;
        if (cutoff > 0) {
          const ts = a.created_at ? new Date(a.created_at).getTime() : 0;
          return ts >= cutoff;
        }
        return true;
      })
      .map(a => {
        const winnerName = a.winning_user_id ? playersMap.get(a.winning_user_id) : null;
        return {
          cost: a.winning_bid as number,
          date: a.created_at ? new Date(a.created_at).getTime() : 0,
          formattedDate: a.created_at ? new Date(a.created_at).toLocaleDateString() : 'Unknown',
          draftId: a.draft_id,
          winner: winnerName || 'Guest'
        };
      });

    const legacySales = (stats.legacy || [])
      .filter(l => {
        const isMatch = !excludedPokemonNames.has(l.pokemon) && resolveIdentity(l.pokemon, '').key === pokemonKey;
        if (!isMatch) return false;
        if (cutoff > 0 && l.date) {
          return new Date(l.date).getTime() >= cutoff;
        }
        return true;
      })
      .map(l => {
        const costStr = String(l.cost).replace(/[^0-9]/g, '');
        const cost = parseInt(costStr, 10);
        return {
          cost: isNaN(cost) ? 0 : cost,
          date: l.date ? new Date(l.date).getTime() : 0,
          formattedDate: l.date ? new Date(l.date).toLocaleDateString() : 'Legacy Draft',
          draftId: 'Legacy',
          winner: 'Guest'
        };
      });

    // Combine and sort chronologically
    const allSales = [...legacySales, ...auctionSales].sort((a, b) => a.date - b.date);

    if (allSales.length === 0) return null;

    // 2. Statistical Outlier Detection (align with PokemonStatsTab logic)
    // Ignore $100 bids for the bound calculation
    const filteredForBounds = allSales.map(s => s.cost).filter(c => c !== 100);
    let iqr = 0;
    let lowerBound: number | null = null;
    let upperBound: number | null = null;

    if (filteredForBounds.length > 1) {
      const sortedCosts = [...filteredForBounds].sort((a, b) => a - b);
      const q1 = calculateQuantile(sortedCosts, 0.25);
      const q3 = calculateQuantile(sortedCosts, 0.75);
      iqr = q3 - q1;
      
      lowerBound = q1 - 1.5 * iqr;
      upperBound = q3 + 2.0 * iqr;
    }

    const data = allSales.map((s, index) => ({
      saleNumber: index + 1,
      cost: s.cost,
      isOutlier: s.cost === 100 || (lowerBound !== null && s.cost < lowerBound) || (upperBound !== null && s.cost > upperBound),
      date: s.formattedDate,
      draftId: s.draftId,
      winner: s.winner
    }));

    // Robust axis: clip only points that sit more than a large margin beyond
    // the IQR fences. The Tukey "far out" distance (1.5x IQR past the whisker)
    // is the industry standard here: near-outliers that merely tickle the fence
    // stay on-scale so the axis hugs the bulk of the data; truly extreme sales
    // are pinned to the matching chart edge as hollow rings. Hover shows the
    // true value.
    const maxCost = data.reduce((max, d) => (d.cost > max ? d.cost : max), 0);
    const minCost = data.reduce((min, d) => (d.cost < min ? d.cost : min), Infinity);
    const clipGap = iqr * 1.5; // Tukey far-out threshold from each fence
    const topClipStart = upperBound !== null ? upperBound + clipGap : maxCost;
    const bottomClipEnd = lowerBound !== null ? lowerBound - clipGap : minCost;

    // Axis limits based on visible (non-clipped) points
    const visible = data.filter((d) => d.cost <= topClipStart && d.cost >= bottomClipEnd);
    const maxVisible = visible.length > 0
      ? visible.reduce((m, d) => Math.max(m, d.cost), 0)
      : maxCost;
    const minVisible = visible.length > 0
      ? visible.reduce((m, d) => Math.min(m, d.cost), Infinity)
      : minCost;
    const axisMax = maxVisible > 0 ? Math.ceil(maxVisible * 1.04) + 1 : 1;
    const axisMin = Math.max(0, Math.floor(minVisible * 0.96) - 1);
    const pinnedTop = axisMax - (axisMax - axisMin) * 0.015;   // just inside top edge
    const pinnedBottom = axisMin + (axisMax - axisMin) * 0.015; // just inside bottom edge

    const plotted = data.map((d) => {
      if (d.cost > topClipStart) {
        return { ...d, realCost: d.cost, cost: pinnedTop, clipSide: 'top' as const };
      }
      if (d.cost < bottomClipEnd) {
        return { ...d, realCost: d.cost, cost: pinnedBottom, clipSide: 'bottom' as const };
      }
      return d;
    });

    const xAxisTicks = [];
    const tickInterval = data.length > 50 ? 10 : 5;
    for (let i = tickInterval; i <= data.length; i += tickInterval) {
      xAxisTicks.push(i);
    }
    if (xAxisTicks.length === 0 && data.length > 0) xAxisTicks.push(1);

    return { data: plotted, lowerBound, upperBound, axisMin, axisMax, yAxisTicks: niceYTicks(axisMin, axisMax), xAxisTicks };
  }, [pokemonKey, stats]);

  if (!chartData || chartData.data.length === 0) return null;

  const { data, lowerBound, upperBound, axisMin, axisMax, yAxisTicks, xAxisTicks } = chartData;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 5, right: 40, left: 10, bottom: 25 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis 
          dataKey="saleNumber" 
          stroke="#666" 
          tick={{ fontSize: 11 }}
          ticks={xAxisTicks}
          label={{ value: 'Sale #', position: 'insideBottom', offset: -15, fill: '#666', fontSize: 11 }}
        />
        <YAxis 
          stroke="#666" 
          tick={{ fontSize: 11 }}
          domain={[axisMin, axisMax]}
          ticks={yAxisTicks}
          label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 11 }}
        />
        <Tooltip content={<CustomTooltip />} />
        
        {(upperBound !== null && upperBound > axisMin && upperBound < axisMax) && <ReferenceLine y={upperBound} stroke="#8B0000" strokeDasharray="5 5" label={{ value: 'Outlier Bound', position: 'right', fill: '#8B0000', fontSize: 10 }} />}
        {(lowerBound !== null && lowerBound > axisMin && lowerBound < axisMax) && <ReferenceLine y={lowerBound} stroke="#8B0000" strokeDasharray="5 5" />}

        <Line 
          type="monotone" 
          dataKey="cost" 
          stroke="#36A2EB" 
          strokeWidth={0}
          dot={(props: any) => {
            const { cx, cy, payload } = props;
            if (payload.clipSide) {
              return <circle key={`dot-${payload.saleNumber}`} cx={cx} cy={cy} r={5} fill="none" stroke="#8B0000" strokeWidth={2} />;
            }
            const fill = payload.isOutlier ? '#8B0000' : '#36A2EB'; // Dark red for outliers
            return (
              <circle key={`dot-${payload.saleNumber}`} cx={cx} cy={cy} r={4} fill={fill} stroke="none" />
            );
          }}
          activeDot={{ r: 6, fill: '#fff', stroke: '#36A2EB' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Standalone snapshot used in the Draft Breakdown tab.
const PokemonPriceHistoryChart: React.FC<PokemonHistoryChartProps> = ({ pokemonKey, pokemonName, stats, cutoffDate }) => {
  return (
    <div className="pokemon-price-history-chart" style={{ width: '100%', height: '280px', marginTop: '10px', background: 'rgba(0,0,0,0.1)', borderRadius: '8px', padding: '15px', boxSizing: 'border-box' }}>
      <h4 style={{ margin: '0 0 15px', fontSize: '1rem', color: '#888', textAlign: 'center', fontWeight: 600 }}>
        Price History: {pokemonName}
      </h4>
      <PokemonPriceHistoryChartBody pokemonKey={pokemonKey} pokemonName={pokemonName} stats={stats} cutoffDate={cutoffDate} />
    </div>
  );
};

export { PokemonPriceHistoryChartBody };
export default PokemonPriceHistoryChart;